import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { theme } from './theme.js';
// ─── Context ──────────────────────────────────────────────────────────────────
const ToastContext = createContext(null);
// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used inside <ToastProvider>');
    }
    return ctx;
};
// ─── Single Toast Item ────────────────────────────────────────────────────────
const toastSlideIn = `
  @keyframes toastSlideIn {
    from {
      transform: translateX(110%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes toastSlideOut {
    from {
      transform: translateX(0);
      opacity: 1;
      maxHeight: 80px;
      marginBottom: 8px;
    }
    to {
      transform: translateX(110%);
      opacity: 0;
      maxHeight: 0;
      marginBottom: 0;
    }
  }
`;
const typeConfig = {
    success: {
        border: theme.colors.success.primary,
        label: 'Success',
        icon: (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "8", cy: "8", r: "7", stroke: theme.colors.success.primary, strokeWidth: "1.5" }), _jsx("path", { d: "M5 8l2 2 4-4", stroke: theme.colors.success.primary, strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })] })),
    },
    error: {
        border: theme.colors.danger.primary,
        label: 'Error',
        icon: (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "8", cy: "8", r: "7", stroke: theme.colors.danger.primary, strokeWidth: "1.5" }), _jsx("path", { d: "M5.5 5.5l5 5M10.5 5.5l-5 5", stroke: theme.colors.danger.primary, strokeWidth: "1.5", strokeLinecap: "round" })] })),
    },
    warning: {
        border: theme.colors.warning.primary,
        label: 'Warning',
        icon: (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M8 2L14.5 13H1.5L8 2z", stroke: theme.colors.warning.primary, strokeWidth: "1.5", strokeLinejoin: "round" }), _jsx("path", { d: "M8 6v3.5M8 11v.5", stroke: theme.colors.warning.primary, strokeWidth: "1.5", strokeLinecap: "round" })] })),
    },
    info: {
        border: theme.colors.info.primary,
        label: 'Info',
        icon: (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "8", cy: "8", r: "7", stroke: theme.colors.info.primary, strokeWidth: "1.5" }), _jsx("path", { d: "M8 7v4.5M8 5v.5", stroke: theme.colors.info.primary, strokeWidth: "1.5", strokeLinecap: "round" })] })),
    },
};
const ToastCard = ({ item, onDismiss }) => {
    const config = typeConfig[item.type];
    const cardStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing.md,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.bg.secondary,
        border: `1px solid ${theme.colors.border.secondary}`,
        borderLeft: `3px solid ${config.border}`,
        borderRadius: theme.radius.md,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        width: '320px',
        maxWidth: 'calc(100vw - 32px)',
        animation: 'toastSlideIn 280ms cubic-bezier(0.4, 0, 0.2, 1) both',
        pointerEvents: 'all',
    };
    const iconWrapStyle = {
        flexShrink: 0,
        marginTop: '2px',
        display: 'flex',
        alignItems: 'center',
    };
    const bodyStyle = {
        flex: 1,
        minWidth: 0,
    };
    const titleStyle = {
        fontSize: theme.typography.fontSize.sm.size,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text.primary,
        margin: 0,
        fontFamily: theme.typography.fontFamily.primary,
    };
    const messageStyle = {
        fontSize: theme.typography.fontSize.sm.size,
        color: theme.colors.text.secondary,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginTop: theme.spacing.xs,
        fontFamily: theme.typography.fontFamily.primary,
        lineHeight: theme.typography.fontSize.sm.lineHeight,
        wordBreak: 'break-word',
    };
    const dismissBtnStyle = {
        flexShrink: 0,
        background: 'none',
        border: 'none',
        padding: '2px',
        cursor: 'pointer',
        color: theme.colors.text.muted,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.radius.sm,
        lineHeight: 0,
        marginTop: '1px',
    };
    return (_jsxs("div", { style: cardStyle, role: "alert", "aria-live": "assertive", "aria-atomic": "true", children: [_jsx("div", { style: iconWrapStyle, "aria-hidden": "true", children: config.icon }), _jsxs("div", { style: bodyStyle, children: [_jsx("p", { style: titleStyle, children: config.label }), _jsx("p", { style: messageStyle, children: item.message })] }), _jsx("button", { type: "button", style: dismissBtnStyle, onClick: () => onDismiss(item.id), "aria-label": "Dismiss notification", children: _jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M2 2l10 10M12 2L2 12", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" }) }) })] }));
};
// ─── Provider ─────────────────────────────────────────────────────────────────
export const ToastProvider = ({ children, maxToasts = 5 }) => {
    const [toasts, setToasts] = useState([]);
    const counterRef = useRef(0);
    // Auto-dismiss timers, cleared on unmount so none fires setState on a tree that
    // is gone (KAN-147: one outlived its test environment and failed the web suite).
    const timersRef = useRef(new Set());
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            for (const t of timers)
                clearTimeout(t);
            timers.clear();
        };
    }, []);
    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    const toast = useCallback((message, type = 'info', duration = 4000) => {
        counterRef.current += 1;
        const id = `toast-${counterRef.current}-${Date.now()}`;
        const item = { id, message, type, duration };
        setToasts((prev) => {
            const next = [...prev, item];
            // Keep most recent maxToasts items
            return next.length > maxToasts ? next.slice(next.length - maxToasts) : next;
        });
        if (duration > 0) {
            const timer = setTimeout(() => {
                timersRef.current.delete(timer);
                dismiss(id);
            }, duration);
            timersRef.current.add(timer);
        }
        return id;
    }, [dismiss, maxToasts]);
    const portalStyle = {
        position: 'fixed',
        bottom: theme.spacing.xl,
        right: theme.spacing.xl,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.sm,
        zIndex: theme.zIndex.tooltip,
        pointerEvents: 'none',
        alignItems: 'flex-end',
    };
    return (_jsxs(ToastContext.Provider, { value: { toast, dismiss }, children: [_jsx("style", { children: toastSlideIn }), children, _jsx("div", { "aria-label": "Notifications", style: portalStyle, children: toasts.map((item) => (_jsx(ToastCard, { item: item, onDismiss: dismiss }, item.id))) })] }));
};
//# sourceMappingURL=Toast.js.map